import { type IOrderListItem } from '@/types/IOrder';

/**
 * Порядковый номер заказа — для порядка «новые сверху» там, где сервер не
 * сортирует за нас: в live-слое над выдачей и в сравнении событий сокета.
 *
 * Раньше это был `id`. С PAR-5189 `id` не приходит ниоткуда, но никуда и не
 * делся: последний сегмент `formated_id` (`2608-2260-45100` → `45100`) — это он
 * и есть. Запасной источник — `id` прежнего бэкенда. Заказ без того и другого
 * получает ноль и уходит в конец, а не роняет сортировку `NaN`: с ним любое
 * сравнение ложно, и порядок развалился бы молча.
 *
 * @param order - заказ с `formated_id` и, возможно, `id`
 * @returns порядковый номер либо `0`
 */
export const getOrderSequence = (
  order: Pick<IOrderListItem, 'formated_id' | 'id'>
): number => {
  const segments = (order.formated_id ?? '').split('-').filter(Boolean);
  const tail = segments[segments.length - 1];
  const fromNumber =
    tail !== undefined && /^\d+$/.test(tail) ? Number(tail) : Number.NaN;

  if (Number.isSafeInteger(fromNumber)) {
    return fromNumber;
  }

  return typeof order.id === 'number' && Number.isFinite(order.id)
    ? order.id
    : 0;
};

/**
 * Номер заказа для показа: `formated_id`, иначе `№ {id}`, если он пришёл,
 * иначе прочерк — а не `№ undefined` в заголовке тоста.
 *
 * @param order - заказ
 * @returns строка номера
 */
export const getOrderNumberLabel = (
  order: Pick<IOrderListItem, 'formated_id' | 'id'>
): string => {
  const formated = order.formated_id?.trim() ?? '';

  if (formated !== '') {
    return formated;
  }

  return order.id !== undefined ? `№ ${order.id}` : '—';
};
